import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Loader2, Phone, MapPin, Save, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import api, { formatApiError } from '../api';
import { INTERVENTION_STATUS, INTERVENTION_TYPES, URGENCY_LEVELS, formatDate } from '../constants';

const Field = ({ label, value }) => (
  <div>
    <p className="text-xs text-neutral-500">{label}</p>
    <p className="font-medium text-neutral-900">{value || '-'}</p>
  </div>
);

const AdminInterventionDetail = () => {
  const { interventionId } = useParams();
  const [it, setIt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('');
  const [urgency, setUrgency] = useState('');
  const [type, setType] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/interventions/${interventionId}`);
      setIt(data);
      setStatus(data.status); setUrgency(data.urgency); setType(data.type);
      setAdminNote(data.adminNote || '');
    } catch (e) {
      toast.error('Intervention introuvable');
    } finally {
      setLoading(false);
    }
  }, [interventionId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/interventions/${interventionId}`, { status, urgency, type, adminNote });
      toast.success('Intervention mise à jour');
      fetchData();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !it) {
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-red-700" /></div>;
  }

  const st = INTERVENTION_STATUS[it.status] || {};

  return (
    <div data-testid="admin-intervention-detail">
      <Link to="/admin/interventions" className="inline-flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 mb-4">
        <ArrowLeft className="w-4 h-4" /> Retour aux demandes
      </Link>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <h1 className="text-2xl font-bold text-neutral-900">{it.number}</h1>
        <span className={`text-sm px-3 py-1 rounded ${st.color}`}>{st.label}</span>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h2 className="font-bold text-neutral-900 mb-4">Détails de la demande</h2>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Client" value={<Link to={`/admin/clients/${it.clientId}`} className="text-red-700 hover:underline">{it.clientName}</Link>} />
              <Field label="Type" value={INTERVENTION_TYPES[it.type]} />
              <Field label="Urgence" value={URGENCY_LEVELS[it.urgency]?.label} />
              <Field label="Date souhaitée" value={`${it.preferredDate || '-'} ${it.preferredSlot || ''}`} />
              <Field label="Contact sur place" value={it.contactName} />
              <Field label="Téléphone" value={it.contactPhone && <a href={`tel:${it.contactPhone}`} className="text-red-700">{it.contactPhone}</a>} />
            </div>
            <div className="mt-4">
              <Field label="Adresse" value={<span className="flex items-center gap-1"><MapPin className="w-4 h-4" />{it.address} {it.postalCode} {it.city}</span>} />
            </div>
            {it.accessInstructions && <div className="mt-4"><Field label="Consignes d'accès" value={it.accessInstructions} /></div>}
            <div className="mt-4">
              <p className="text-xs text-neutral-500 mb-1">Description</p>
              <p className="text-neutral-800 whitespace-pre-wrap bg-neutral-50 rounded-lg p-3">{it.description}</p>
            </div>
            {it.contactPhone && (
              <a href={`tel:${it.contactPhone}`} className="inline-block mt-4">
                <Button className="bg-red-700 hover:bg-red-600"><Phone className="w-4 h-4 mr-2" />Appeler le client</Button>
              </a>
            )}
          </div>

          <div className="bg-white rounded-xl border border-neutral-200 p-5">
            <h2 className="font-bold text-neutral-900 mb-4 flex items-center gap-2"><Clock className="w-4 h-4" />Historique</h2>
            <div className="space-y-3">
              {(it.history || []).slice().reverse().map((h, i) => (
                <div key={i} className="flex gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-red-600 mt-1.5 shrink-0" />
                  <div>
                    <p className="text-neutral-800">{h.action} {h.newStatus && <span className="text-neutral-500">→ {INTERVENTION_STATUS[h.newStatus]?.label || h.newStatus}</span>}</p>
                    <p className="text-xs text-neutral-400">{formatDate(h.at)} · {h.user}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl border border-neutral-200 p-5 space-y-4 lg:sticky lg:top-6">
            <h2 className="font-bold text-neutral-900">Gestion</h2>
            <div>
              <Label>Statut</Label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(INTERVENTION_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Urgence</Label>
              <Select value={urgency} onValueChange={setUrgency}>
                <SelectTrigger data-testid="urgency-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(URGENCY_LEVELS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger data-testid="type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(INTERVENTION_TYPES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Note interne</Label>
              <Textarea value={adminNote} onChange={(e) => setAdminNote(e.target.value)} rows={3} data-testid="admin-note" />
            </div>
            <Button onClick={save} disabled={saving} className="w-full bg-red-700 hover:bg-red-600" data-testid="save-intervention-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminInterventionDetail;
